import React from 'react'
/* eslint-disable import/no-extraneous-dependencies */
import { I18n } from '@aws-amplify/core'
import PropTypes from 'prop-types'
import { useSnackbar } from 'notistack'
import { useNavigate, Link } from '@reach/router'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Divider from '@material-ui/core/Divider'
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone'
import NewReleasesIcon from '@material-ui/icons/NewReleases'
import Settings from '@material-ui/icons/Settings'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { MaterialSignOut } from '../security'
import { useLayoutContext } from '../context'
import { ACCEPTED_COGNITO_GROUPS } from '../../config'
import {
  getBrandsByOwnerAndTrademarkStatus,
  getNotificationsByOwnerAndCreatedAt,
} from '../../services'

const acceptedCognitoGroups = JSON.parse(ACCEPTED_COGNITO_GROUPS)

const useStyles = makeStyles((theme) => ({
  menuItem: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
  },
  menuItemSelected: {
    textDecoration: 'none',
    '&:link, &:visited, &:active': {
      textDecoration: 'none',
    },
    '& > div': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      color: theme.palette.primary.main,
    },
  },
}))

const NavLink = (props) => {
  const classes = useStyles()

  return (
    <Link
      {...props}
      getProps={({ isCurrent }) => {
        // the object returned here is passed to the
        // anchor element's props
        return {
          className: clsx(
            classes.menuItem,
            isCurrent && classes.menuItemSelected
          ),
        }
      }}
    />
  )
}

const SideBarContent = (props) => {
  const {
    showNotifications,
    setShowNotifications,
    showTrademarks,
    setShowTrademarks,
    setShowTrademarkDetail,
  } = useLayoutContext()
  const {
    currentUser,
    isAuthenticated,
    setOpened,
    setCollapsed,
    clearData,
  } = props
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const disableActiveComponents = () => {
    setShowNotifications(false)
    setShowTrademarks(false)
    setShowTrademarkDetail(false)
    navigate('/')
  }

  const userHasRegisteredBrands = async () => {
    const brands = await getBrandsByOwnerAndTrademarkStatus(
      currentUser.username
    )
    return brands
  }

  const userHasNotifications = async () => {
    const notifications = await getNotificationsByOwnerAndCreatedAt(
      currentUser.username
    )
    return notifications
  }

  let denyAcces = true
  if (acceptedCognitoGroups) {
    acceptedCognitoGroups.forEach((cg) => {
      if (
        currentUser?.signInUserSession?.accessToken?.payload?.[
          'cognito:groups'
        ]?.indexOf(cg) > -1
      ) {
        denyAcces = false
      }
    })
  }

  const list = [
    {
      primaryText: 'Notifications',
      icon: <NotificationsNoneIcon />,
      onClickItem: async () => {
        const userNotifications = await userHasNotifications()
        if (userNotifications.length > 0) {
          /* eslint-disable no-unused-expressions */
          showNotifications ? navigate('/') : navigate('/notifications')
          setShowTrademarkDetail(false)
          setShowTrademarks(false)
          setOpened(false)
          setShowNotifications(!showNotifications)
        } else {
          enqueueSnackbar('No hay ninguna notificación', {
            preventDuplicate: true,
            variant: 'info',
          })
        }
      },
      to: '/',
    },
    {
      primaryText: 'Trademarks',
      icon: <NewReleasesIcon />,
      onClickItem: async () => {
        const registeredBrands = await userHasRegisteredBrands()
        if (registeredBrands.length > 0) {
          setShowNotifications(false)
          setShowTrademarkDetail(false)
          setOpened(false)
          setShowTrademarks(true)
          /* eslint-disable no-unused-expressions */
          showTrademarks ? navigate('/') : navigate('/trademarks')
          setShowTrademarks(!showTrademarks)
        } else {
          enqueueSnackbar('No hay ninguna marca registrada', {
            preventDuplicate: true,
            variant: 'info',
          })
        }
      },
      to: '/',
    },
  ]

  return (
    <List>
      {!denyAcces &&
        list.map(({ primaryText, icon, onClickItem }) => {
          return (
            <ListItem key={primaryText} button onClick={onClickItem}>
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={I18n.get(primaryText)} />
            </ListItem>
          )
        })}
      {isAuthenticated ? (
        <>
          {!denyAcces && (
            <>
              <Divider />
              <NavLink key="Settings" to="/settings">
                <ListItem button>
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText primary={I18n.get('Settings')} />
                </ListItem>
              </NavLink>
            </>
          )}
          <MaterialSignOut
            variant="contained"
            selected={false}
            setOpenedMenu={setOpened}
            setCollapsedMenu={setCollapsed}
            disableNotificationsView={setShowNotifications}
            clearData={clearData}
            disableTrademarksAndNotifications={disableActiveComponents}
          />
        </>
      ) : null}
    </List>
  )
}

SideBarContent.propTypes = {
  currentUser: PropTypes.shape({
    username: PropTypes.string,
    attributes: PropTypes.shape({
      email: PropTypes.string,
      email_verified: PropTypes.bool,
      sub: PropTypes.string,
    }),
    signInUserSession: PropTypes.shape({
      accessToken: PropTypes.shape({
        payload: PropTypes.arrayOf([PropTypes.string]),
      }),
    }),
  }),
  isAuthenticated: PropTypes.bool,
  setOpened: PropTypes.func,
  setCollapsed: PropTypes.func,
  clearData: PropTypes.func,
}
SideBarContent.defaultProps = {
  currentUser: undefined,
  isAuthenticated: false,
  setOpened: () => {},
  setCollapsed: () => {},
  clearData: () => {},
}

export default SideBarContent
